<template>
    <header class="page-title">
        <h1><i class="bi bi-cc"></i> お支払い</h1>
    </header>

    <section class="section">
        <h2 class="section-title">お支払状況</h2>
        <div class="row mb-3 align-items-end">
            <div class="col-6">
                <label class="form-label">お支払設定</label>
                <div class="form-readonly">{{ company.payment_status_display }}</div>
            </div>
            <div class="col-6">
                <label class="form-label">利用期限</label>
                <div class="form-readonly">{{ company.expired ? dateYmd(company.expired) : 'なし' }}</div>
            </div>
            <div class="col-6" v-if="company.payment_status === PaymentStatus.CONTINUOUS">
                <button type="button" class="btn btn-outline-danger" @click="stopAutoRenew" :disabled="registering">
                    自動更新を停止
                </button>
            </div>
        </div>
    </section>

    <section class="section">
        <h2 class="section-title">プラン</h2>
        <div class="row mb-3 align-items-end">
            <div class="col-12">
                <label class="form-label">現在のプラン</label>
                <div class="form-readonly">{{ company.price_type_display }}</div>
            </div>
            <div class="col-6" v-if="$store.getters['auth/canPay']()">
                <button class="btn btn-outline-primary" @click="is_upgrading_plan = true">プランを変更</button>
            </div>
        </div>
    </section>

    <section class="section" v-if="shouldShowCreditCardForm && $store.getters['auth/canPay']()">
        <h2 v class="section-title">
            クレジットカード{{ company.payment_status === PaymentStatus.CONTINUOUS ? '変更' : '登録' }}
        </h2>
        <div class="row">
            <div class="col-12">
                <div class="mb-3">
                    <label class="form-label">カード番号</label>
                    <div id="payjp-card-element" style="border: 1px solid #ced4da; padding: 10px; border-radius: 5px;">
                        <!-- PayJP Card Element がここにマウントされます -->
                    </div>
                </div>
                <div class="mb-5">
                    <label class="form-label">カード名義 (半角英数字)</label>
                    <form-input v-model="card_holder" placeholder="TARO YAMADA" />
                </div>
                <div class="mb-3">
                    <button type="button" class="btn btn-primary" @click="registerCreditCard" :disabled="!cardElement || registering">
                        <span v-if="registering">登録中...</span>
                        <span v-else>クレジットカードを{{ company.payment_status === PaymentStatus.CONTINUOUS ? '変更' : '登録' }}</span>
                    </button>
                </div>
            </div>
        </div>
    </section>
    <div class="alert alert-warning" v-else>
        プランの変更、クレジットカードの登録には「支払」権限が必要です。
    </div>

    <transition name="fade">
        <div v-if="is_upgrading_plan">
            <div class="modal-backdrop show"></div>
            <div class="modal" tabindex="-1" style="display:block">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <i class="bi bi-exclamation-diamond-fill fs-5 text-warning"></i>&nbsp;
                            <h5 class="modal-title">プランを変更</h5>
                            <button type="button" class="btn-close" @click="is_upgrading_plan = false;"></button>
                        </div>
                        <div class="modal-body p-5">
                            <div class="alert alert-warning">次回更新時から新しい料金が適用されます。</div>
                            <label class="form-label">変更後プラン</label>
                            <form-select v-model="new_price_type" :options="PriceType.options()"></form-select>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" @click="is_upgrading_plan = false;">キャンセル</button>
                            <button type="button" class="btn btn-primary" @click="upgradePlan()">変更</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import FormSelect from '@/components/forms/FormSelect';
import FormInput from '@/components/forms/FormInput';
import Company from "@/models/entities/company";
import PaymentStatus from "@/models/enums/payment-status";
import PriceType from "@/models/enums/price-type";
import { dateYmd } from '@/utilities/date-format'

export default {
    name: 'PageSystemPayment',
    components: {
        FormSelect,
        FormInput
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage'
    ],
    data() {
        return {
            loading: true,
            company: new Company(),
            payjpInstance: null,
            elements: null,
            cardElement: null,
            card_holder: null,
            registering: false,
            is_upgrading_plan: false,
            new_price_type: null,
            PriceType,
            dateYmd,
        }
    },
    computed: {
        PaymentStatus() {
            return PaymentStatus;
        },
        shouldShowCreditCardForm() {
            return this.company.payment_status === PaymentStatus.TRIAL ||
                   this.company.payment_status === PaymentStatus.MANUAL ||
                   this.company.payment_status  === PaymentStatus.CONTINUOUS;
        }
    },
    mounted() {
        this.$http.get('/me')
        .then((response) => {
            this.company = new Company(response.data.company);
            this.new_price_type = this.company.price_type;
            this.loading = false;

            // 該当ステータスの場合のみPayJP SDKを読み込み
            if (this.shouldShowCreditCardForm) {
                this.loadPayjpScript().then(() => {
                    this.initializePayjp();
                }).catch(error => {
                    console.error('PayJP SDK読み込みエラー:', error);
                });
            }
        })
    },
    methods: {
        loadPayjpScript() {
            return new Promise((resolve, reject) => {
                if (window.Payjp) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = 'https://js.pay.jp/v2/pay.js';

                script.onload = () => {
                    resolve();
                };

                script.onerror = () => {
                    console.error('PayJP script loading failed');
                    reject(new Error('PayJP script loading failed'));
                };

                document.head.appendChild(script);
            });
        },
        initializePayjp() {
            try {
                // PayJPインスタンスを作成
                this.payjpInstance = window.Payjp(process.env.VUE_APP_PAYJP_PUBLIC_KEY);

                // Elementsインスタンスを作成
                this.elements = this.payjpInstance.elements();

                // カード要素を作成
                this.cardElement = this.elements.create('card');

                // カード要素をDOMにマウント
                this.cardElement.mount('#payjp-card-element');

            } catch (error) {
                console.error("PayJP初期化エラー:", error);
            }
        },
        registerCreditCard() {
            if (!this.payjpInstance || !this.cardElement) {
                return;
            }

            if (!this.card_holder) {
                this.showErrorMessage('カード名義を入力してください');
                return;
            }
            if (this.card_holder.length < 2 && this.card_holder.length > 45) {
                this.showErrorMessage('カード名義は2文字以上、45文字以内で入力してください');
                return;
            }
            const regex = /^[a-zA-Z0-9 .-]+$/;
            if (!regex.test(this.card_holder)) {
                this.showErrorMessage('カード名義は半角英数字、スペース、ピリオド、ハイフンのみが入力できます');
                return;
            }

            this.registering = true;

            try {
                const tokenData = {
                    card: {
                        name: this.card_holder,
                        email: this.$store.state.auth.email,
                        phone: this.company.phone_e164
                    },
                    three_d_secure: true, // 3Dセキュアを有効化
                };

                this.payjpInstance.createToken(this.cardElement, tokenData).then(result => {
                    if (result.error) {
                        this.showErrorMessage(result.error.message + ' (TOKEN_FAILED)');
                        this.registering = false;
                    } else {
                        // 3D Secure 成功
                        const token = result.id;
                        const card = result.card;

                        if (card && card.three_d_secure_status && (card.three_d_secure_status === 'verified' || card.three_d_secure_status === 'error')) {
                            this.submitCardToken(token);
                        } else {
                            // ここに来ることはありえないはず
                            this.showErrorMessage('予期せぬエラーが発生しました (TOKEN_ERROR_01)');
                        }
                    }
                }).catch(error => {
                    this.showErrorMessage('予期せぬエラーが発生しました (TOKEN_ERROR_02)');
                    console.error(error);
                    this.registering = false;
                });
            } catch (error) {
                this.showErrorMessage('予期せぬエラーが発生しました (TOKEN_ERROR_03)');
                console.error(error);
                this.registering = false;
            }
        },
        submitCardToken(token) {
            this.startScreenLoading();
            // /payment/charge
            this.$http.post('/payment/charge', {
                token: token,
            })
            .then(() => {
                this.endScreenLoading();
                this.showMessage('クレジットカードの登録が完了しました');
                this.cardElement.clear();
                this.registering = false;

                // 決済成功後に会社情報を再取得して画面を更新
                this.refreshCompanyInfo();
            })
            .catch((error) => {
                this.endScreenLoading();
                console.error('決済処理エラー:', error);
                this.registering = false;
            });
        },
        refreshCompanyInfo() {
            this.$http.get('/me')
            .then((response) => {
                this.company = new Company(response.data.company);
                this.new_price_type = this.company.price_type;
            })
            .catch((error) => {
                console.error('会社情報更新エラー:', error);
                this.showErrorMessage('会社情報の更新に失敗しました');
            });
        },
        stopAutoRenew() {
            this.startScreenLoading();
            this.$http.post('/payment/cancel')
            .then((response) => {
                this.showMessage('自動更新の停止が完了しました');
                this.company = new Company(response.data.company);
                this.new_price_type = this.company.price_type;
            }).finally(() => {
                this.endScreenLoading();
            });
        },
        upgradePlan() {
            this.startScreenLoading();
            this.$http.post('/payment/upgrade', { price_type: this.new_price_type })
            .then((response) => {
                this.showMessage('プランを変更しました');
                this.company = new Company(response.data.company);
                this.new_price_type = this.company.price_type;
            }).finally(() => {
                this.is_upgrading_plan = false;
                this.endScreenLoading();
            });
        }
    },
}
</script>

<style scoped>
</style>
